"use client";

import { cn } from "@/lib/utils";
import { clientLogger } from "@/lib/logger";
import { useAppData } from "@/store/appStore";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { PRODUCT_NAME_CAR } from "@/constants/productTypes";
import { GTMEvent, trackEvent } from "@/lib/utils/gtm-events";

const CitySelector = ({ type }: { type?: string }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedCity, setSelectedCity] = useState<City>();
	const [appData, setData, , hasHydrated] = useAppData();
	const containerRef = useRef<HTMLDivElement>(null);
	const [dropdownPosition, setDropdownPosition] = useState({
		top: 0,
		left: 0,
		width: 0,
	});
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	// Derive the target city from props and app data
	const targetCity = useMemo(() => {
		if (!hasHydrated || appData?.cityList?.length === 0) return null;

		const trimmedCityId = appData?.cityId?.trim();
		const matchedCity = trimmedCityId
			? appData?.cityList?.find((city) => city?.id === trimmedCityId)
			: undefined;
		const fallbackCity = appData?.cityList?.[0];
		return matchedCity ?? fallbackCity;
	}, [hasHydrated, appData?.cityId, appData?.cityList]);

	// Update selectedCity when targetCity changes
	useEffect(() => {
		if (!targetCity) return;

		setSelectedCity(targetCity);
		if (appData?.cityId !== targetCity.id) {
			setData({ cityId: targetCity.id });
		}
	}, [targetCity, appData?.cityId, setData]);

	const updateDropdownPosition = () => {
		if (containerRef.current) {
			const rect = containerRef.current.getBoundingClientRect();
			setDropdownPosition({
				top: rect.bottom + 8,
				left: rect.left,
				width: rect.width,
			});
		}
	};

	const handleToggle = () => {
		if (!isOpen) {
			updateDropdownPosition();
		}
		setIsOpen(!isOpen);
		clientLogger.info("City selector toggled", {
			component: "CitySelector",
			isOpen: !isOpen,
			type,
		});
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (
			containerRef.current &&
			!containerRef.current.contains(event.target as Node)
		) {
			setIsOpen(false);
		}
	};

	useEffect(() => {
		if (isOpen) {
			document.addEventListener("click", handleClickOutside);
		}
		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, [isOpen]);

	const handleCitySelector = (city: City) => {
		const oldCityName = selectedCity?.name || "unknown";
		setIsOpen(false);
		setSelectedCity(city);
		setData({ cityId: city?.id });
		trackEvent(GTMEvent.LOCATION_CHANGED, {
			old_city: oldCityName,
			new_city: city.name,
		});
		clientLogger.info("City selected", {
			component: "CitySelector",
			cityId: city?.id,
			cityName: city?.name,
			type,
		});
		window.location.reload();
	};

	const isCarContext = appData?.onboardingCompletionType === PRODUCT_NAME_CAR;

	// Hide city selector if not in car context
	if (!isCarContext) {
		return null;
	}

	const dropdown =
		isOpen && mounted ? (
			<div
				className="fixed flex flex-col bg-black rounded-lg shadow-xl border border-gray-800 city-selector text-sm overflow-hidden z-[999999]"
				style={{
					top: `${dropdownPosition.top}px`,
					left: `${dropdownPosition.left}px`,
					minWidth: `${dropdownPosition.width}px`,
				}}
			>
				{appData?.cityList?.map((city: City, i: number) => (
					<div
						key={city.id}
						className={cn(
							"px-4 py-3 hover:bg-gray-700/40 cursor-pointer whitespace-nowrap overflow-hidden font-neon-light-sm",
							i === 0 && "rounded-t-lg",
							appData?.cityList?.length &&
								i === appData.cityList.length - 1 &&
								"rounded-b-lg",
						)}
						onClick={() => handleCitySelector(city)}
					>
						{city?.name}
					</div>
				))}
			</div>
		) : null;

	return (
		<>
			<div ref={containerRef} className="relative inline-block">
				<div
					className="flex items-center gap-2 cursor-pointer mr-2"
					onClick={handleToggle}
				>
					<div className="flex justify-center items-center w-9 h-9 flex-shrink-0 aspect-square rounded-[16px] bg-automint-dark">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="15"
							height="19"
							viewBox="0 0 15 19"
							fill="none"
						>
							<path
								d="M14.1556 7.48991C14.1556 11.6507 9.53977 15.9841 7.98977 17.3224C7.84537 17.431 7.6696 17.4897 7.48893 17.4897C7.30827 17.4897 7.1325 17.431 6.9881 17.3224C5.4381 15.9841 0.822266 11.6507 0.822266 7.48991C0.822266 5.7218 1.52464 4.02611 2.77489 2.77586C4.02513 1.52562 5.72082 0.823242 7.48893 0.823242C9.25704 0.823242 10.9527 1.52562 12.203 2.77586C13.4532 4.02611 14.1556 5.7218 14.1556 7.48991Z"
								stroke="#F1FB76"
								strokeWidth="1.64632"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M7.48893 9.98991C8.86965 9.98991 9.98893 8.87062 9.98893 7.48991C9.98893 6.1092 8.86965 4.98991 7.48893 4.98991C6.10822 4.98991 4.98893 6.1092 4.98893 7.48991C4.98893 8.87062 6.10822 9.98991 7.48893 9.98991Z"
								stroke="#F1FB76"
								strokeWidth="1.64632"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</div>
					<span className="font-neon-light-sm">{selectedCity?.name}</span>
				</div>
			</div>

			{mounted && dropdown && createPortal(dropdown, document.body)}
		</>
	);
};

export default CitySelector;
